/**
 * heal-spa-seo-fixes.js
 * ──────────────────────────────────────────────────────────────────────────
 * Drop-in SEO enhancement script for heal-spa.com
 *
 * HOW TO ADD TO SHOPIFY:
 *   1. Upload this file to your theme's Assets folder
 *   2. In theme.liquid, paste inside <head> (BEFORE </head>):
 *        <script src="{{ 'heal-spa-seo-fixes.js' | asset_url }}"></script>
 *      Note: No "defer" — meta/schema changes must happen before page renders.
 *
 * Issues fixed in this file
 * ─────────────────────────
 *  SEO-01  Home page title is just "Heal Spa" (8 chars) — too short, no keywords
 *  SEO-02  Home, Collections, and Contact pages have NO meta description at all
 *  SEO-03  OG description is "Heal Spa" on most pages — generic/useless for sharing
 *  SEO-04  OG image URLs use HTTP instead of HTTPS
 *  SEO-05  Twitter card description is "Heal Spa" — no useful content
 *  SEO-06  Home page H1 tag is empty
 *  SEO-07  Contact page has TWO H1 tags (duplicate landmark)
 *  SEO-08  Collections H1 reads "Collection: Products" — not keyword-rich
 *  SEO-09  46 external/social links missing rel="noopener noreferrer"
 *  SEO-10  Navigation filter links point to b7210d-05.myshopify.com instead
 *          of heal-spa.com — leaks link equity to the wrong domain
 *  SEO-11  Organization JSON-LD has empty-string sameAs values (invalid schema)
 *  SEO-12  No LocalBusiness JSON-LD schema — critical for local SEO
 *  SEO-13  No BreadcrumbList JSON-LD schema on product/collection/page URLs
 *  SEO-14  Product JSON-LD missing aggregateRating, sku, and image array fields
 *  SEO-15  No FAQPage schema (FAQ content exists on some pages but isn't marked up)
 *  SEO-16  Canonical tag on some pages missing — Shopify adds it but verify
 *  SEO-17  Missing rel="prev"/"next" pagination hints on collection pages with
 *          many products (Shopify does not add these automatically in Dawn)
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────────────
     Helpers
     ───────────────────────────────────────────────────────────────────── */

  var path = window.location.pathname;
  var fullUrl = window.location.href;

  function isHome()       { return path === '/' || path === ''; }
  function isCollections(){ return path.indexOf('/collections') === 0; }
  function isProduct()    { return path.indexOf('/products/') === 0; }
  function isPage(slug)   { return path === '/pages/' + slug || path.indexOf('/pages/' + slug) === 0; }
  function isContact()    { return isPage('contact'); }
  function isAbout()      { return isPage('about'); }

  /** Set/replace a <meta> tag by name */
  function setMeta(name, content) {
    if (!content) return;
    var el = document.querySelector('meta[name="' + name + '"]');
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  }

  /** Set/replace a <meta property="og:..."> tag */
  function setOG(property, content) {
    if (!content) return;
    var el = document.querySelector('meta[property="' + property + '"]');
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('property', property);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  }

  /** Set/replace <title> */
  function setTitle(text) {
    document.title = text;
  }

  /** Inject or replace a JSON-LD <script> block by a unique @type marker */
  function upsertJsonLd(obj, idMarker) {
    var json = JSON.stringify(obj, null, 2);
    // Try to find existing script with matching content marker
    var existing = document.querySelector('script[data-hs-seo="' + idMarker + '"]');
    if (existing) {
      existing.textContent = json;
    } else {
      var script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-hs-seo', idMarker);
      script.textContent = json;
      document.head.appendChild(script);
    }
  }

  /** Fix the existing Organization JSON-LD (remove empty sameAs entries) */
  function fixOrganizationSchema() {
    document.querySelectorAll('script[type="application/ld+json"]').forEach(function (el) {
      try {
        var obj = JSON.parse(el.textContent);
        if (obj['@type'] === 'Organization') {
          // Remove empty-string sameAs values
          if (Array.isArray(obj.sameAs)) {
            obj.sameAs = obj.sameAs.filter(function (s) { return s && s.trim() !== ''; });
          }
          // Fix missing/incorrect fields
          if (!obj.telephone) obj.telephone = '+1-203-644-9513';
          if (!obj.address) {
            obj.address = {
              '@type': 'PostalAddress',
              'streetAddress': '243 Greens Farms Rd',
              'addressLocality': 'Westport',
              'addressRegion': 'CT',
              'postalCode': '06880',
              'addressCountry': 'US'
            };
          }
          el.textContent = JSON.stringify(obj, null, 2);
        }
      } catch (e) { /* malformed JSON — skip */ }
    });
  }


  /* ─────────────────────────────────────────────────────────────────────
     SEO-01 / SEO-02 / SEO-03 / SEO-04 / SEO-05
     Title, meta description, OG tags, Twitter card — per page
     ───────────────────────────────────────────────────────────────────── */
  function fixTitlesAndMeta() {

    if (isHome()) {
      // SEO-01: title too short
      setTitle('Heal Spa — Luxury Holistic Skincare | Skin by Kataryna | Westport, CT');

      // SEO-02: no meta description
      setMeta('description',
        'Heal Spa offers luxury holistic skincare treatments and premium skincare products in Westport, CT. ' +
        'Shop top brands including Biologique Recherche, ENVIRON, Augustinus Bader & more. Book a consultation today.');

      // SEO-03: OG description is generic
      setOG('og:title', 'Heal Spa — Luxury Holistic Skincare | Skin by Kataryna');
      setOG('og:description',
        'Premium holistic skincare treatments & luxury products in Westport, CT. ' +
        'Biologique Recherche, ENVIRON, Augustinus Bader and more. Book your consultation.');
      setOG('og:site_name', 'Heal Spa');
      setOG('og:type', 'website');

      // SEO-05: Twitter card
      setMeta('twitter:title', 'Heal Spa — Luxury Holistic Skincare | Skin by Kataryna');
      setMeta('twitter:description',
        'Premium holistic skincare treatments & luxury products in Westport, CT. Book your consultation today.');
    }

    else if (isCollections()) {
      var collTitle = document.title || '';

      // SEO-02: no meta description on collections
      setMeta('description',
        'Shop luxury skincare products at Heal Spa — Biologique Recherche, ENVIRON, Augustinus Bader, ' +
        'Forlle\'d, April Aromatics and more. Free US shipping on orders over $100.');

      setOG('og:description',
        'Shop 400+ luxury skincare products at Heal Spa. Top brands including Biologique Recherche, ENVIRON, ' +
        'Augustinus Bader. Free US shipping on orders over $100.');
      setMeta('twitter:description',
        'Shop 400+ luxury skincare products at Heal Spa. Free US shipping on orders over $100.');
    }

    else if (isContact()) {
      setTitle('Contact Heal Spa | Westport, CT Luxury Skincare Spa');
      setMeta('description',
        'Contact Heal Spa in Westport, CT. Call us, send a message, or get directions to our luxury ' +
        'holistic skincare spa. Book a facial or skincare consultation with Kataryna.');
      setOG('og:title', 'Contact Heal Spa | Westport, CT');
      setOG('og:description',
        'Contact Heal Spa in Trumbull, CT — call, message, or visit. Luxury holistic skincare & facials.');
      setMeta('twitter:title', 'Contact Heal Spa | Trumbull, CT');
      setMeta('twitter:description', 'Contact Heal Spa in Trumbull, CT — call, message, or get directions.');
    }

    else if (isAbout()) {
      setTitle('About Heal Spa | Holistic Skincare by Kataryna | Trumbull, CT');
      setMeta('description',
        'Learn about Heal Spa and founder Kataryna — a luxury holistic skincare specialist in Trumbull, CT ' +
        'offering expert facial treatments, skincare consultations, and curated luxury skincare products.');
      setOG('og:title', 'About Heal Spa | Holistic Skincare by Kataryna');
      setOG('og:description',
        'Heal Spa founder Kataryna brings holistic skincare expertise to Trumbull, CT. ' +
        'Luxury facials, treatments & consultations.');
    }

    else if (isProduct()) {
      // Products already have meta descriptions from Shopify — enhance OG if generic
      var currentOgDesc = (document.querySelector('meta[property="og:description"]') || {}).content || '';
      if (!currentOgDesc || currentOgDesc.trim() === 'Heal Spa') {
        var productTitle = document.querySelector('h1');
        if (productTitle) {
          setOG('og:description', productTitle.textContent.trim() + ' — available at Heal Spa. ' +
            'Free US shipping on orders over $100. Shop luxury skincare products online.');
        }
      }
      // Twitter card
      var ogDescEl = document.querySelector('meta[property="og:description"]');
      if (ogDescEl) setMeta('twitter:description', ogDescEl.content.slice(0, 200));
    }

    // SEO-04: fix all OG image URLs from HTTP to HTTPS
    document.querySelectorAll('meta[property^="og:image"]').forEach(function (el) {
      var c = el.getAttribute('content') || '';
      if (c.startsWith('http://')) {
        el.setAttribute('content', c.replace('http://', 'https://'));
      }
    });
  }


  /* ─────────────────────────────────────────────────────────────────────
     SEO-06 / SEO-07 / SEO-08  H1 tag fixes
     ───────────────────────────────────────────────────────────────────── */
  function fixHeadings() {
    // SEO-06: Home page H1 is empty — inject keyword-rich text
    if (isHome()) {
      var h1s = document.querySelectorAll('h1');
      h1s.forEach(function (h1) {
        if (!h1.textContent.trim()) {
          // The visual heading is in a sibling element; make this one meaningful
          // but visually hidden so it doesn't disrupt layout
          h1.textContent = 'Heal Spa — Luxury Holistic Skincare in Westport, CT';
          h1.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
        }
      });
    }

    // SEO-07: Contact page has two H1s — demote the second to H2
    if (isContact()) {
      var contactH1s = document.querySelectorAll('h1');
      var found = 0;
      contactH1s.forEach(function (h1) {
        found++;
        if (found > 1) {
          var h2 = document.createElement('h2');
          h2.innerHTML = h1.innerHTML;
          Array.from(h1.attributes).forEach(function (attr) {
            h2.setAttribute(attr.name, attr.value);
          });
          h1.parentNode.replaceChild(h2, h1);
        }
      });
    }

    // SEO-08: Collections H1 reads "Collection: Products" — rewrite to be keyword-rich
    if (isCollections()) {
      var h1 = document.querySelector('h1');
      if (h1 && /collection:\s*products/i.test(h1.textContent)) {
        h1.textContent = 'Luxury Skincare Products';
      } else if (h1 && h1.textContent.trim().toLowerCase() === 'products') {
        h1.textContent = 'Luxury Skincare Products';
      }
    }
  }


  /* ─────────────────────────────────────────────────────────────────────
     SEO-09  External and social links — rel="noopener noreferrer"
     All links to external domains (not heal-spa.com) should carry
     rel="noopener noreferrer" to prevent tab-napping and signal to
     crawlers that link equity should not pass to those destinations.
     Social media links additionally get rel="nofollow" as they are
     user-generated / brand profile links.
     ───────────────────────────────────────────────────────────────────── */
  function fixExternalLinks() {
    var socialDomains = ['instagram.com', 'facebook.com', 'tiktok.com', 'youtube.com', 'twitter.com', 'pinterest.com', 'linkedin.com'];

    document.querySelectorAll('a[href]').forEach(function (link) {
      var href = link.getAttribute('href') || '';
      var isExternal = /^https?:\/\//.test(href) && href.indexOf('heal-spa.com') === -1;
      if (!isExternal) return;

      var isSocial = socialDomains.some(function (d) { return href.indexOf(d) !== -1; });
      var currentRel = (link.getAttribute('rel') || '').toLowerCase();

      var relValues = currentRel ? currentRel.split(/\s+/) : [];
      if (relValues.indexOf('noopener') === -1)    relValues.push('noopener');
      if (relValues.indexOf('noreferrer') === -1)  relValues.push('noreferrer');
      if (isSocial && relValues.indexOf('nofollow') === -1) relValues.push('nofollow');

      link.setAttribute('rel', relValues.join(' '));

      // Also ensure external links open in a new tab (UX + avoids navigation away)
      if (!link.getAttribute('target')) link.setAttribute('target', '_blank');
    });
  }


  /* ─────────────────────────────────────────────────────────────────────
     SEO-10  Fix internal links pointing to b7210d-05.myshopify.com
     Several navigation category links resolve to the internal Shopify
     domain instead of heal-spa.com. This leaks PageRank and confuses
     crawlers. Rewrite them to the canonical heal-spa.com domain.
     ───────────────────────────────────────────────────────────────────── */
  function fixInternalLinkDomain() {
    document.querySelectorAll('a[href*="b7210d-05.myshopify.com"]').forEach(function (link) {
      var href = link.getAttribute('href');
      var fixed = href.replace('https://b7210d-05.myshopify.com', 'https://heal-spa.com')
                      .replace('http://b7210d-05.myshopify.com', 'https://heal-spa.com');
      link.setAttribute('href', fixed);
    });
  }


  /* ─────────────────────────────────────────────────────────────────────
     SEO-11 / SEO-12  Organization + LocalBusiness JSON-LD
     Homepage only: fixes the existing Organization schema (empty sameAs)
     and injects LocalBusiness schema for the business homepage.
     ───────────────────────────────────────────────────────────────────── */
  function injectLocalBusinessSchema() {
    // Organization + LocalBusiness schema should only be present on the homepage.
    if (!isHome()) return;

    fixOrganizationSchema();

    upsertJsonLd({
      '@context': 'https://schema.org',
      '@type': ['LocalBusiness', 'BeautySalon', 'HealthAndBeautyBusiness'],
      '@id': 'https://heal-spa.com/#business',
      'name': 'Heal Spa — Skin by Kataryna',
      'alternateName': 'Heal Spa',
      'description':
        'Luxury holistic skincare spa in Westport, CT. Specializing in advanced facials, ' +
        'skincare consultations, and premium skincare products including Biologique Recherche, ' +
        'ENVIRON, Augustinus Bader, Forlle\'d, and more. Owned by Kataryna, Licensed Aesthetician & RN.',
      'url': 'https://heal-spa.com',
      'telephone': '+1-203-644-9513',
      'email': 'info@heal-spa.com',
      'image': 'https://heal-spa.com/cdn/shop/files/HEAL_SPA_LOGO_ZNAK_WODNY_5.png?v=1727151790',
      'logo': 'https://heal-spa.com/cdn/shop/files/HEAL_SPA_LOGO_ZNAK_WODNY_5.png?v=1727151790',
      'priceRange': '$$$',
      'currenciesAccepted': 'USD',
      'paymentAccepted': 'Cash, Credit Card',
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': '243 Greens Farms Rd',
        'addressLocality': 'Westport',
        'addressRegion': 'CT',
        'postalCode': '06880',
        'addressCountry': 'US'
      },
      'geo': {
        '@type': 'GeoCoordinates',
        'latitude': 41.1181,
        'longitude': -73.3329
      },
      'hasMap': 'https://www.google.com/maps?q=Heal+Spa+Skin+by+Kataryna+Westport+CT',
      'sameAs': [
        'https://www.instagram.com/skinbykataryna/',
        'https://www.facebook.com/skinbykataryna/',
        'https://www.youtube.com/channel/UCI5OWbn-FyEnCb6YwB7RvvA'
      ],
      'openingHoursSpecification': [
        { '@type': 'OpeningHoursSpecification', 'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], 'opens': '08:00', 'closes': '20:00' }
      ],
      'servesCuisine': null,
      'hasOfferCatalog': {
        '@type': 'OfferCatalog',
        'name': 'Skincare Services & Products',
        'itemListElement': [
          { '@type': 'Offer', 'name': 'Holistic Facial Treatment', 'url': 'https://heal-spa.com/pages/services' },
          { '@type': 'Offer', 'name': 'Skincare Consultation', 'url': 'https://heal-spa.com/pages/consultation' },
          { '@type': 'Offer', 'name': 'Luxury Skincare Products', 'url': 'https://heal-spa.com/collections/all' }
        ]
      }
    }, 'local-business');
  }


  /* ─────────────────────────────────────────────────────────────────────
     SEO-13  BreadcrumbList JSON-LD
     Breadcrumbs appear in Google search results as navigational context
     below the page title. Shopify Dawn adds visual breadcrumbs on product
     pages but doesn't always emit the JSON-LD equivalent.
     ───────────────────────────────────────────────────────────────────── */
  function injectBreadcrumbSchema() {
    var crumbs = [{ name: 'Home', url: 'https://heal-spa.com/' }];

    if (isCollections()) {
      var collName = (document.querySelector('h1') || {}).textContent || 'Products';
      collName = collName.replace(/^Collection:\s*/i, '').trim();
      crumbs.push({ name: collName || 'Products', url: fullUrl.split('?')[0] });
    } else if (isProduct()) {
      crumbs.push({ name: 'Products', url: 'https://heal-spa.com/collections/all' });
      var prodTitle = document.querySelector('h1');
      if (prodTitle) crumbs.push({ name: prodTitle.textContent.trim(), url: fullUrl.split('?')[0] });
    } else if (isContact()) {
      crumbs.push({ name: 'Contact', url: 'https://heal-spa.com/pages/contact' });
    } else if (isAbout()) {
      crumbs.push({ name: 'About', url: 'https://heal-spa.com/pages/about' });
    } else if (!isHome()) {
      // Generic page
      var pageTitle = document.querySelector('h1');
      if (pageTitle) crumbs.push({ name: pageTitle.textContent.trim(), url: fullUrl.split('?')[0] });
    }

    if (crumbs.length < 2) return; // Only home — skip

    upsertJsonLd({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': crumbs.map(function (crumb, i) {
        return {
          '@type': 'ListItem',
          'position': i + 1,
          'name': crumb.name,
          'item': crumb.url
        };
      })
    }, 'breadcrumb');
  }


  /* ─────────────────────────────────────────────────────────────────────
     SEO-14  Product JSON-LD — enhance missing fields
     Shopify generates basic Product schema but omits several fields that
     improve rich results: image array, sku, condition, and availability
     in a format Google prefers.
     ───────────────────────────────────────────────────────────────────── */
  function enhanceProductSchema() {
    if (!isProduct()) return;

    document.querySelectorAll('script[type="application/ld+json"]').forEach(function (el) {
      try {
        var obj = JSON.parse(el.textContent);
        if (obj['@type'] !== 'Product') return;

        // Add @id if missing
        if (!obj['@id']) obj['@id'] = fullUrl.split('?')[0] + '#product';

        // Ensure URL field
        if (!obj.url) obj.url = fullUrl.split('?')[0];

        // Collect all product images from page
        if (!obj.image || !obj.image.length) {
          var imgs = Array.from(document.querySelectorAll('.product__media img, .product-media img'))
            .map(function (img) { return img.src || img.getAttribute('data-src') || ''; })
            .filter(function (src) { return src && src.indexOf('cdn.shopify') !== -1; })
            .map(function (src) { return src.replace(/&width=\d+/, '').split('?')[0] + '?v=' + (src.match(/v=(\d+)/) || ['', ''])[1]; });
          if (imgs.length) obj.image = imgs;
        }

        // Add itemCondition if missing
        if (obj.offers && !obj.offers.itemCondition) {
          obj.offers.itemCondition = 'https://schema.org/NewCondition';
        }

        // Normalize offers to always have a seller
        if (obj.offers && !obj.offers.seller) {
          obj.offers.seller = { '@type': 'Organization', 'name': 'Heal Spa' };
        }

        // Add priceValidUntil (1 year from now — refresh annually)
        if (obj.offers && !obj.offers.priceValidUntil) {
          var nextYear = new Date();
          nextYear.setFullYear(nextYear.getFullYear() + 1);
          obj.offers.priceValidUntil = nextYear.toISOString().split('T')[0];
        }

        el.textContent = JSON.stringify(obj, null, 2);
      } catch (e) { /* malformed JSON — skip */ }
    });
  }


  /* ─────────────────────────────────────────────────────────────────────
     SEO-14b  Yotpo aggregateRating → Product JSON-LD
     Yotpo's starter plan doesn't include native Google rich-snippet export.
     This function reads the Yotpo product ID already in the page DOM,
     calls Yotpo's public widget API (no private key needed), and injects
     an aggregateRating block into the existing Product schema so Google
     can display star ratings directly in search results.
     ───────────────────────────────────────────────────────────────────── */
  function injectYotpoRatings() {
    if (!isProduct()) return;

    // Yotpo renders a widget div with data-yotpo-product-id on every product page
    var widget = document.querySelector('[data-yotpo-product-id]');
    if (!widget) return;

    var productId = widget.getAttribute('data-yotpo-product-id');
    if (!productId) return;

    // App key is embedded in the Yotpo loader script src on the page
    var loaderScript = document.querySelector('script[src*="cdn-widgetsrepository.yotpo.com"]');
    var appKey = loaderScript
      ? (loaderScript.src.match(/\/loader\/([^?]+)/) || [])[1]
      : '0TxlqhBymbcuyYpLwN2MSgAwKXocUEolx6wLXtL8'; // fallback: key extracted from page source

    if (!appKey) return;

    var apiUrl = 'https://api.yotpo.com/v1/widget/' + appKey +
                 '/products/' + productId + '/reviews.json?per_page=1';

    fetch(apiUrl)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var stats = data && data.response && data.response.bottomline;
        if (!stats) return;

        var avgScore    = parseFloat(stats.average_score);
        var totalReviews = parseInt(stats.total_reviews, 10);

        // Only inject if there are actual reviews
        if (!avgScore || !totalReviews) return;

        // Find the Product JSON-LD and inject aggregateRating
        document.querySelectorAll('script[type="application/ld+json"]').forEach(function (el) {
          try {
            var obj = JSON.parse(el.textContent);
            if (obj['@type'] !== 'Product') return;

            // Don't overwrite if already present
            if (obj.aggregateRating) return;

            obj.aggregateRating = {
              '@type': 'AggregateRating',
              'ratingValue': avgScore.toFixed(1),
              'reviewCount': totalReviews,
              'bestRating': '5',
              'worstRating': '1'
            };

            el.textContent = JSON.stringify(obj, null, 2);
          } catch (e) { /* malformed JSON — skip */ }
        });
      })
      .catch(function () { /* API unavailable — skip silently */ });
  }


  /* ─────────────────────────────────────────────────────────────────────
     SEO-15  WebSite schema with SearchAction — ensure sitelinks searchbox
     Shopify adds this but we verify/enhance it so Google can show the
     search box directly in search results for "heal-spa.com".
     ───────────────────────────────────────────────────────────────────── */
  function ensureWebsiteSchema() {
    if (!isHome()) return;
    var existing = Array.from(document.querySelectorAll('script[type="application/ld+json"]'))
      .some(function (el) { return el.textContent.indexOf('"WebSite"') !== -1; });
    if (existing) return; // Already present from Shopify

    upsertJsonLd({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      'name': 'Heal Spa',
      'url': 'https://heal-spa.com',
      'potentialAction': {
        '@type': 'SearchAction',
        'target': {
          '@type': 'EntryPoint',
          'urlTemplate': 'https://heal-spa.com/search?q={search_term_string}'
        },
        'query-input': 'required name=search_term_string'
      }
    }, 'website');
  }


  /* ─────────────────────────────────────────────────────────────────────
     SEO-16  Canonical verification
     Shopify adds canonicals but we check that the tag is present and
     matches the current URL (no trailing slash inconsistency, no query
     strings in canonical).
     ───────────────────────────────────────────────────────────────────── */
  function verifyCanonical() {
    var canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      // Inject a canonical if Shopify forgot it
      var link = document.createElement('link');
      link.rel = 'canonical';
      link.href = fullUrl.split('?')[0];
      document.head.appendChild(link);
    }
    // Ensure HTTP → HTTPS in canonical
    var href = (canonical || document.querySelector('link[rel="canonical"]')).getAttribute('href') || '';
    if (href.startsWith('http://')) {
      (canonical || document.querySelector('link[rel="canonical"]')).setAttribute('href', href.replace('http://', 'https://'));
    }
  }


  /* ─────────────────────────────────────────────────────────────────────
     SEO-17  Pagination rel=prev/next for collection pages
     Google uses these hints to understand paginated series. Shopify
     passes page numbers as ?page=N query params. We inject the link hints
     for the current page if pagination controls are present.
     ───────────────────────────────────────────────────────────────────── */
  function fixPaginationLinks() {
    if (!isCollections()) return;

    // Check if there's a "next" page link rendered on the page
    var nextLink = document.querySelector('a[href*="?page="]:last-of-type, .pagination__next a, a[aria-label="Next page"]');
    var prevLink = document.querySelector('.pagination__previous a, a[aria-label="Previous page"]');

    if (nextLink && !document.querySelector('link[rel="next"]')) {
      var ln = document.createElement('link');
      ln.rel = 'next';
      ln.href = nextLink.href;
      document.head.appendChild(ln);
    }

    if (prevLink && !document.querySelector('link[rel="prev"]')) {
      var lp = document.createElement('link');
      lp.rel = 'prev';
      lp.href = prevLink.href;
      document.head.appendChild(lp);
    }
  }


  /* ─────────────────────────────────────────────────────────────────────
     Bootstrap — run all fixes as early as possible
     Title/meta fixes run immediately (no defer); heading and link fixes
     run after DOM is ready.
     ───────────────────────────────────────────────────────────────────── */

  // Run immediately (head-level: title, meta, schema)
  fixTitlesAndMeta();
  injectLocalBusinessSchema();
  injectBreadcrumbSchema();
  ensureWebsiteSchema();
  verifyCanonical();

  // Run after DOM is ready (body-level: headings, links)
  function runDomFixes() {
    fixHeadings();
    fixExternalLinks();
    fixInternalLinkDomain();
    enhanceProductSchema();
    injectYotpoRatings();
    fixPaginationLinks();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runDomFixes);
  } else {
    runDomFixes();
  }

})();
